import { useCallback, useEffect } from 'react';
import { useDispatch as useReduxDispatch, useSelector as useReduxSelector, batch as reduxBatch } from 'react-redux';
import { flatten, isEmpty } from 'lodash';
import { addListener } from '@client/listeners/lib';
import {
  getStore, defaultPool, type RootState, type AppDispatch,
} from '../store';
import type { AddListener } from '@client/listeners/lib';
import type { AnyAction, UnsubscribeListener } from '@reduxjs/toolkit';
import type { TypedUseSelectorHook } from 'react-redux';

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useDispatch = () => useReduxDispatch<AppDispatch>();
export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;
export const usePooledDispatch = () => (action: AnyAction) => defaultPool.dispatch(action);

// Re-export batch for ease of use
export const batch = reduxBatch;

interface TypedUseInstanceSelectorHook<TState> {
  <TSelected>(
    selector: (state: TState, props: unknown) => TSelected,
    props: unknown
  ): TSelected;
}

/**
 * Wraps useSelector so that it can also include an optional property
 */
export const useInstanceSelector: TypedUseInstanceSelectorHook<RootState> = (selector, props: unknown): typeof selectorData => {
  const selectorWithProps = useCallback(
    (state: RootState) => selector(state, props),
    [props, selector],
  );

  const selectorData = useSelector(selectorWithProps);

  return selectorData;
};

type ListenerFunction = (func: AddListener) => ReturnType<AddListener>[];

export const useListeners = (...args: ListenerFunction[]) => {
  useEffect(() => {
    const store = getStore();
    const listeners = flatten((args || []).filter((func) => func).map((func) => func(addListener)));

    if (!isEmpty(listeners) && Array.isArray(listeners)) {
      const unsubscribeFunctions = listeners.map((listener) => store.dispatch(listener) as unknown as UnsubscribeListener);

      return () => {
        unsubscribeFunctions.forEach((unsubscribe) => {
          unsubscribe();
        });
      };
    }

    return undefined;
  });
};
